/**
 * @file Submission UI — SubmissionStagesFormReadonly.
 * Read-only submission stages (contributor dataset overview + admin submission body).
 * Mirrors Admin Submission Details: CMS form fields, resolved labels (not raw ids where possible).
 */

import React from "react";
import type { FormField } from "../../interfaces/formInterfaces";
import {
  isBackgroundApiOnlyUnitObjectKey,
  readBackgroundValueForFormField,
} from "./backgroundStageFieldOrder";
import {
  labelForKey,
  readRowValueForFormField,
  resolveDisplayValue,
  valueForField,
} from "./readonlyFormDisplay";
import { resolveUploadAssetUrl } from "../../utils/resolveUploadAssetUrl";
import DatasetOverviewFormFlow from "./datasetOverview/DatasetOverviewFormFlow";
import "./SubmissionStagesFormReadonly.css";
import {
  fieldsForStage,
  STAGE_SUMMARY_READONLY_FIELDS,
} from "./stageFormSchema";

export { fieldsForStage, fieldsForSubSection, STAGE_SUMMARY_READONLY_FIELDS } from "./stageFormSchema";
export {
  resolveDisplayValue,
  labelForKey,
  valueForField,
  readRowValueForFormField,
} from "./readonlyFormDisplay";

/** Type definition — Any Obj. */
export type AnyObj = { [k: string]: any };

/** Exported const — HIDDEN FIELD KEYS. */
export const HIDDEN_FIELD_KEYS = new Set([
  "id",
  "submissionid",
  "submission_id",
  "backgrounddataid",
  "background_data_id",
  "createdat",
  "created_at",
  "updatedat",
  "updated_at",
  "contributorid",
  "contributor_id",
]);

/** Type definition — Field Schema. */
export type FieldSchema = {
  name?: string;
  label?: string;
  type?: string;
  /** CMS display order — same as contributor Background form (`allFields.sort`). */
  order?: number;
  dropdown?: {
    options?: Array<{ label?: string; value?: string | number; id?: number }>;
  } | null;
};

/** Type definition — Reference Labels. */
export type ReferenceLabels = {
  units?: Record<string, string>;
  materials?: Record<string, string>;
  subMaterials?: Record<string, string>;
  rawMaterials?: Record<string, string>;
  extractionProcesses?: Record<string, string>;
  manufacturingProcesses?: Record<string, string>;
  rawMaterialQualityAttributes?: Record<string, string>;
  technologies?: Record<string, string>;
  manufacturers?: Record<string, string>;
  vehicleCategories?: Record<string, string>;
};

function displayEntries(obj: AnyObj | null | undefined) {
  if (!obj) return [];
  return Object.entries(obj).filter(
    ([k]) => !HIDDEN_FIELD_KEYS.has(String(k || "").toLowerCase())
  );
}

function isBackgroundSubmissionUploadKey(key: string): boolean {
  const k = key.toLowerCase().replace(/_/g, "");
  return (
    k === "materialimageurl" ||
    k === "publicationfileurl" ||
    k === "materialimage" ||
    k === "publicationfile"
  );
}

function isStoredUploadPath(v: unknown): boolean {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (!s) return false;
  if (/^https?:\/\//i.test(s)) return true;
  return s.startsWith("/uploads/");
}

function isLikelyImageUploadPath(path: string, fieldKey: string): boolean {
  if (fieldKey.toLowerCase().includes("materialimage")) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(path);
}

const ReadOnlyField: React.FC<{ label: string; value?: any }> = ({ label, value }) => {
  const v =
    value === null || typeof value === "undefined"
      ? ""
      : typeof value === "string"
        ? value
        : typeof value === "number"
          ? String(value)
          : typeof value === "boolean"
            ? value
              ? "Yes"
              : "No"
            : Array.isArray(value)
              ? value.length
                ? JSON.stringify(value)
                : ""
              : typeof value === "object"
                ? JSON.stringify(value)
                : String(value);

  return (
    <div className="ro-field">
      <label>{label}</label>
      <input value={v} readOnly />
    </div>
  );
};

function uploadFieldDisplayLabel(label: string, fieldKey: string): string {
  const k = fieldKey.toLowerCase().replace(/_/g, "");
  if (k.includes("publicationfile")) return "Publication document";
  if (k.includes("materialimage")) return "Material image";
  return label;
}

const SubmissionUploadAssetField: React.FC<{
  label: string;
  path: string;
  fieldKey: string;
}> = ({ label, path, fieldKey }) => {
  const resolved = resolveUploadAssetUrl(path);
  const showThumb = isLikelyImageUploadPath(path, fieldKey);
  const displayLabel = uploadFieldDisplayLabel(label, fieldKey);

  return (
    <div className="ro-field ro-field--upload">
      <label>{displayLabel}</label>
      <div className="ro-field-upload-actions">
        {showThumb ? (
          <a
            href={resolved}
            target="_blank"
            rel="noopener noreferrer"
            className="ro-field-upload-thumb-wrap"
          >
            <img src={resolved} alt="" className="ro-field-upload-thumb" />
          </a>
        ) : (
          <a
            href={resolved}
            target="_blank"
            rel="noopener noreferrer"
            className="ro-field-upload-link"
          >
            View document
          </a>
        )}
      </div>
    </div>
  );
};

const DataObjectFields: React.FC<{
  obj?: AnyObj | null;
  fields?: FieldSchema[];
  references?: ReferenceLabels;
  onlySchemaFields?: boolean;
  /** Map background unit fields to API `*UnitId` columns. */
  backgroundFieldValues?: boolean;
}> = ({
  obj,
  fields,
  references,
  onlySchemaFields = true,
  backgroundFieldValues = false,
}) => {
  const effectiveFields = fields && fields.length > 0 ? fields : undefined;
  const readValue = (name: string) => {
    if (backgroundFieldValues) {
      const bg =
        readBackgroundValueForFormField(
          obj as Record<string, unknown> | null | undefined,
          name
        );
      if (bg !== undefined && bg !== null && bg !== "") return bg;
    }
    return readRowValueForFormField(obj, name) ?? valueForField(obj, name);
  };

  const schemaEntries =
    effectiveFields
      ?.filter((f) => f.name && !HIDDEN_FIELD_KEYS.has(String(f.name).toLowerCase()))
      .map((f) => {
        const name = String(f.name);
        const rawVal = readValue(name);
        return [name, rawVal, f.label || labelForKey(name)] as const;
      }) ?? [];

  const schemaNames = new Set(
    schemaEntries.flatMap(([name]) => [
      name,
      name.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()),
    ])
  );

  const extraEntries = displayEntries(obj).filter(([name]) => {
    if (schemaNames.has(name)) return false;
    if (backgroundFieldValues && isBackgroundApiOnlyUnitObjectKey(name)) {
      return false;
    }
    return true;
  });

  const entries =
    onlySchemaFields && effectiveFields
      ? [...schemaEntries]
      : [
          ...schemaEntries,
          ...extraEntries.map(([name, value]) => [name, value, labelForKey(name)] as const),
        ];

  if (entries.length === 0) {
    return <div className="mini-value empty-data-note">No data available.</div>;
  }

  return (
    <div className="dataset-grid">
      {entries.map(([k, value, label]) => {
        const field = effectiveFields?.find((f) => f.name === k);
        const rawPath =
          value != null && (typeof value === "string" || typeof value === "number")
            ? String(value).trim()
            : "";
        const useUploadUi =
          isBackgroundSubmissionUploadKey(k) &&
          rawPath.length > 0 &&
          isStoredUploadPath(rawPath);

        if (useUploadUi) {
          return (
            <SubmissionUploadAssetField
              key={k}
              label={label}
              path={rawPath}
              fieldKey={k}
            />
          );
        }

        return (
          <ReadOnlyField
            key={k}
            label={label}
            value={resolveDisplayValue(k, value, obj, field, references)}
          />
        );
      })}
    </div>
  );
};

/** Type definition — Submission Stages Form Readonly Props. */
export type SubmissionStagesFormReadonlyProps = {
  submission: AnyObj;
  onlySchemaFields?: boolean;
};

/** Exported const — Submission Stages Form Readonly. */
export const SubmissionStagesFormReadonly: React.FC<SubmissionStagesFormReadonlyProps> = ({
  submission,
  onlySchemaFields: _onlySchemaFields = true,
}) => <DatasetOverviewFormFlow submission={submission} />;

/** React component — Submission Stages Form Readonly. */
export default SubmissionStagesFormReadonly;
